import { FC, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
// Components
import {
  Modal,
  Loader,
  ModalTitle,
  PhoneTextField,
  SelectAsync,
  Card,
  MobileTable,
  Table,
  ISelectOption,
  SMBSelect
} from '../../../components';
import { Close, Save, Subject, OtherHouses, DeleteForever, Build, Add } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Fade,
  Button,
  Grid,
  Box,
  useMediaQuery,
  Stack,
  MenuItem,
  Typography,
  Divider
} from '@mui/material';
import {
  IDomainUser,
  IProjectManagerNeighborhood,
  IResponse,
  IProjectManagerActiveNeighborhood,
  IProjectManagerDetail
} from '../../../models';
import { formatDate } from '../../../helpers';
import {
  updateProjectManager,
  createProjectManager,
  getDomainUsers,
  getProjectManagerAvailableNeighborhoods,
  deleteProjectManagerNeighborhood,
  createProjectManagerNeighborhood,
  getProjectManagerDetail
} from '../../../fetch';

interface IProjectManagerModal {
  open: boolean;
  onClose: () => void;
  projectManagerId?: string | number;
  fetchProjectManagers: () => void;
}

const Schema = Yup.object().shape({
  accountName: Yup.string().max(255, 'Max 255 characters').required('Required'),
  displayName: Yup.string().max(255, 'Max 255 characters').required('Required'),
  contactPhoneNumber: Yup.string().max(255, 'Max 255 characters').required('Required')
});

export const ProjectManagerModal: FC<IProjectManagerModal> = ({
  open,
  onClose,
  projectManagerId,
  fetchProjectManagers
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | number>('');
  const [accountOptions, setAccountOptions] = useState<ISelectOption[] | null>(null);
  const [isDeleting, setDeleting] = useState<{ [id: number]: boolean }>({});

  const {
    isLoading: isLoadingProjectManagerAvailableNeighborhoods,
    data: projectMangerNeighborhoods,
    refetch: refetchProjectManagerNeighborhoods
  } = useQuery<IProjectManagerActiveNeighborhood[], Error>(
    ['getProjectManagerAvailableNeighborhoods', projectManagerId],
    () => getProjectManagerAvailableNeighborhoods(projectManagerId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!projectManagerId
    }
  );

  const {
    isLoading: isLoadingProjectManager,
    data: currentProjectManager,
    refetch: refetchProjectManager
  } = useQuery<IProjectManagerDetail, Error>(
    ['getProjectManagerDetail', projectManagerId],
    () => getProjectManagerDetail(projectManagerId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!projectManagerId
    }
  );

  const handleDelete = async (neighborhoodId: number | string) => {
    const result = window.confirm('Are you sure you want to delete this neighborhood?');
    if (result) {
      setDeleting({ [neighborhoodId]: true });
      try {
        await deleteProjectManagerNeighborhood(projectManagerId, neighborhoodId);
        refetchProjectManagerNeighborhoods();
        refetchProjectManager();
        enqueueSnackbar('Neighborhood removed!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Error deleting neighborhood.', {
          variant: 'error'
        });
      } finally {
        setDeleting({ [neighborhoodId]: false });
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Phase',
        accessor: 'phase',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IProjectManagerActiveNeighborhood } };
        }) => {
          return (
            <>
              <LoadingButton
                loading={isDeleting[original.neighborhoodId]}
                loadingPosition="start"
                color="error"
                disabled={isDeleting[original.neighborhoodId]}
                startIcon={<DeleteForever />}
                onClick={() => {
                  handleDelete(original.neighborhoodId);
                }}
              >
                Delete
              </LoadingButton>
            </>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectManagerId, isDeleting]);

  const activeBuildsColumns = useMemo(() => {
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhood',
        sort: false
      },
      {
        Header: 'Lot',
        accessor: 'lot',
        sort: false
      },
      {
        Header: 'Status',
        accessor: 'status',
        sort: false
      },
      {
        Header: 'Frame Date',
        accessor: 'frameDate',
        sort: false,
        Cell: props => <>{formatDate(props.cell.row.original.frameDate)}</>
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          accountName: currentProjectManager?.name ?? '',
          displayName: currentProjectManager?.displayName ?? '',
          contactPhoneNumber: currentProjectManager?.contactPhoneNumber ?? ''
        }}
        validationSchema={Schema}
        onSubmit={async (values, actions) => {
          try {
            if (currentProjectManager && currentProjectManager.projectManagerId) {
              await updateProjectManager(currentProjectManager.projectManagerId, values);
            } else {
              await createProjectManager(values);
            }
            enqueueSnackbar(
              currentProjectManager ? 'Project Manager Updated!' : 'Project Manager Created!',
              {
                variant: 'success'
              }
            );
            onClose();
            fetchProjectManagers();
            actions.resetForm();
          } catch (error) {
            enqueueSnackbar(error?.Detail ?? 'Error saving project manager.', {
              variant: 'error'
            });
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          errors,
          handleSubmit,
          dirty,
          isValid,
          setFieldValue
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  resetForm();
                  onClose();
                }
              }}
              maxWidth="md"
            >
              {(isSubmitting || isLoadingProjectManager) && (
                <Loader type="overlay" position="centered" />
              )}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Box mb={1}>
                    <ModalTitle
                      title={!!projectManagerId ? `Edit Project Manager` : 'Add Project Manager'}
                    />
                  </Box>
                  <Card title="Details" icon={<Subject />}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <SelectAsync
                          name="accountName"
                          label="Active Directory User Account"
                          placeholder="Select"
                          required
                          disabled={!!projectManagerId}
                          apiRequest={() =>
                            getDomainUsers({
                              sortBy: 'Name'
                            })
                          }
                          transformResponse={(response: IResponse<IDomainUser[]>) => {
                            return response.records.map(record => ({
                              label: record.displayName,
                              value: record.accountName
                            }));
                          }}
                          hasClear={!projectManagerId}
                          onClear={() => {
                            setFieldValue('accountName', '');
                            setFieldValue('displayName', '');
                          }}
                          onChange={e => {
                            const selected = accountOptions?.find(a => a.value === e.target.value);
                            if (selected) {
                              setFieldValue('accountName', selected.value);
                              setFieldValue('displayName', selected.label);
                            }
                          }}
                          handleOptions={options => setAccountOptions(options)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <PhoneTextField
                          fullWidth
                          autoComplete="nope"
                          label="Contact Phone Number"
                          name="contactPhoneNumber"
                          required
                        />
                      </Grid>
                    </Grid>
                    <Box marginTop="2rem">
                      <Button
                        disabled={!dirty || isSubmitting || !isValid}
                        type="submit"
                        startIcon={<Save />}
                        variant="contained"
                        color="primary"
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        startIcon={<Close />}
                        color="inherit"
                        onClick={() => {
                          if (!deepEqual(initialValues, values)) {
                            const result = window.confirm(
                              'You have unsaved changes, are you sure you want to exit?'
                            );
                            if (result) {
                              resetForm();
                              onClose();
                            } else {
                              return;
                            }
                          } else {
                            onClose();
                          }
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Card>
                  {!!projectManagerId && (
                    <>
                      <Card title="Neighborhoods" icon={<OtherHouses />}>
                        <Stack
                          gap={1}
                          direction={{
                            xs: 'column',
                            sm: 'row'
                          }}
                          mb={1}
                        >
                          <SMBSelect
                            disabled={isLoadingProjectManagerAvailableNeighborhoods}
                            label={
                              isLoadingProjectManagerAvailableNeighborhoods
                                ? 'Loading...'
                                : 'Neighborhood'
                            }
                            value={selectedNeighborhoodId}
                            size="small"
                            onChange={e => setSelectedNeighborhoodId(e.target.value)}
                            handleClear={() => setSelectedNeighborhoodId('')}
                          >
                            {!isLoadingProjectManagerAvailableNeighborhoods &&
                              projectMangerNeighborhoods?.length === 0 && (
                                <MenuItem disabled>No neighborhoods available</MenuItem>
                              )}
                            {!isLoadingProjectManagerAvailableNeighborhoods &&
                              projectMangerNeighborhoods?.map((record, index) => {
                                return (
                                  <MenuItem key={`${index}`} value={record.neighborhoodId}>
                                    {record.name} Phase {record.phase}
                                  </MenuItem>
                                );
                              })}
                          </SMBSelect>
                          <Button
                            startIcon={<Add />}
                            sx={{ minWidth: '14rem' }}
                            onClick={async () => {
                              try {
                                await createProjectManagerNeighborhood(
                                  projectManagerId,
                                  selectedNeighborhoodId
                                );
                                refetchProjectManagerNeighborhoods();
                                refetchProjectManager();
                                setSelectedNeighborhoodId('');
                                enqueueSnackbar('Neighborhood added!', {
                                  variant: 'success'
                                });
                              } catch (error) {
                                enqueueSnackbar(error?.Detail ?? 'Error creating neighborhood.', {
                                  variant: 'error'
                                });
                              }
                            }}
                            color="primary"
                            variant="contained"
                            disabled={!selectedNeighborhoodId}
                          >
                            Add Neighborhood
                          </Button>
                        </Stack>
                        <Divider />
                        {currentProjectManager?.neighborhoods?.length === 0 && (
                          <Box mt={1}>
                            <Typography align="center">No neighborhoods found.</Typography>
                          </Box>
                        )}
                        {currentProjectManager?.neighborhoods?.length > 0 && (
                          <Table
                            columns={columns}
                            data={currentProjectManager?.neighborhoods ?? []}
                            hidePagination
                            mobileProps={{
                              handleDelete: (val: IProjectManagerNeighborhood) => {
                                handleDelete(val.neighborhoodId);
                              },
                              fields: columns
                                .filter(c => c.Header)
                                .map(c => ({
                                  name: c.Header,
                                  accessor: c.accessor,
                                  Cell: c?.Cell
                                }))
                            }}
                            ResponsiveComponent={isMobile ? MobileTable : undefined}
                          />
                        )}
                      </Card>
                      <Card title="Active Builds" icon={<Build />}>
                        {currentProjectManager?.activeBuilds?.length === 0 && (
                          <Typography align="center">No active builds.</Typography>
                        )}
                        {currentProjectManager?.activeBuilds?.length > 0 && (
                          <Table
                            columns={activeBuildsColumns}
                            data={currentProjectManager?.activeBuilds ?? []}
                            hidePagination
                            mobileProps={{
                              fields: columns
                                .filter(c => c.Header)
                                .map(c => ({
                                  name: c.Header,
                                  accessor: c.accessor,
                                  Cell: c?.Cell
                                }))
                            }}
                            ResponsiveComponent={isMobile ? MobileTable : undefined}
                          />
                        )}
                      </Card>
                    </>
                  )}
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
